// src/hooks/useNotifications.ts
import { useEffect, useState, useRef } from "react";
import connectNotifications from "../../services/notifications/notificationsSocketService";
import { iUserPermissions } from "../../types/permissions";
import { Notification } from "../../types/notification.interface";
import { WS_URL } from "../../config";
import { getFromLocalStorage } from "utils/localStorage/localStorage.utils";
import { refreshWebSocketToken } from "../../services/users/accessService";

const webSocketUrl = WS_URL;

export const useNotifications = () => {
  const [notificationsSocket, setNotifications] = useState<Notification>();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disconnectRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const user = getFromLocalStorage<iUserPermissions>("user");
    if (!user) {
      console.warn("No user data found in localStorage, skipping WebSocket connection");
      return;
    }
    try {
      const token = user.wstoken;
      const rights = user.rights;

      if (!token) {
        console.warn("No WebSocket token available");
        return;
      }

      console.debug(
        `Connecting to WebSocket at ${webSocketUrl ?? "ws://localhost:8080"}`,
      );

      const { disconnect } = connectNotifications(
        webSocketUrl ?? "ws://localhost:8080",
        token,
        rights,
        (message: Notification) => {
          setNotifications(message);
        },
        () => {
          console.debug("WebSocket connected and authenticated");
          setIsConnected(true);
          setError(null);
        },
        (closeEvent: CloseEvent) => {
          console.debug(
            `WebSocket closed: code=${closeEvent.code}, reason=${closeEvent.reason}`,
          );
          setIsConnected(false);
        },
        (errorEvent: Event) => {
          console.error("WebSocket error event:", errorEvent);
          setError(
            `WebSocket error: ${(errorEvent as any)?.message || "Unknown error"}`,
          );
          setIsConnected(false);
        },
        refreshWebSocketToken,
        (message: string) => {
          setError(message);
          setIsConnected(false);
        },
      );

      // Store disconnect function to call on cleanup
      disconnectRef.current = disconnect;

      return () => {
        // Only disconnect if we actually have a connection to clean up
        if (disconnectRef.current) {
          console.debug("Cleaning up WebSocket connection");
          disconnectRef.current();
          disconnectRef.current = null;
        }
        setIsConnected(false);
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error("Error initializing WebSocket:", err);
      setError(`Failed to initialize WebSocket: ${errorMsg}`);
      setIsConnected(false);
    }
  }, []);

  return { notificationsSocket, isConnected, error };
};
